import { getPackingCount } from '../../../../utils/orderHelpers';
import { getFirstBarcode } from './barcodeClassifiers';

export const getScanPackLog = () => {
  return {
    id: '',
    time: new Date(),
    event: '',
    name: '',
    SKU: '',
    updated_at: '',
    increment_id: '',
    order_item_id: '',
    input: '',
    state: '',
    on_ex: 'on GPX',
    Log_count: ''
  };
};

const getActionBarcodeLog = (order, input, userName) => {
  const scannedItems = order.scanned_items || [];
  const unScannedItems = order.unscanned_items || [];
  const firstUnScannedItem = unScannedItems[0];
  const firstScannedItem = scannedItems[0];

  return {
    ...getScanPackLog(),
    input,
    id: order.id,
    order_item_id: firstUnScannedItem?.order_item_id || firstScannedItem?.order_item_id || '',
    rem_qty: firstUnScannedItem?.qty_remaining || null,
    SKU: firstUnScannedItem?.sku || '',
    Log_count: '',
    product_name: '',
    name: userName,
    state: 'scanpack.rfp.default',
    event: 'regular',
    updated_at: order.updated_at,
    increment_id: order.increment_id
  };
};

export const getRestartLog = (order, scanPackSettings, userName) =>
  getActionBarcodeLog(order, scanPackSettings.restart_code, userName);

export const getServiceIssueLog = (order, scanPackSettings, userName) =>
  getActionBarcodeLog(order, scanPackSettings.service_issue_code, userName);

export const getNoteLog = (order, noteFromPacker, userName) => {
  return {
    ...getScanPackLog(),
    message: noteFromPacker,
    id: order.id,
    event: 'note',
    name: userName,
    updated_at: order.updated_at,
    increment_id: order.increment_id
  };
};

export const getTypeScanLog = (order, input, count, event, items, SKU, Box, userName) => {
  return {
    ...getScanPackLog(),
    input,
    id: order.id,
    order_item_id: items?.order_item_id,
    box_id: null,
    state: 'scanpack.rfp.default',
    count,
    event,
    box_name: Box,
    SKU: SKU || '',
    name: userName,
    updated_at: order.updated_at,
    increment_id: order.increment_id
  };
};

export const getScanAllItemLog = (order, input, next_state, event, items, SKU, userName, scanAllCount, scanned_count, product_id_child) => {
  return {
    ...getScanPackLog(),
    input,
    id: order.id,
    order_item_id: items?.order_item_id,
    state: next_state,
    event,
    SKU: SKU || '',
    name: userName,
    updated_at: order.updated_at,
    increment_id: order.increment_id,
    total_qty: scanAllCount - scanned_count,
    product_id: product_id_child
  };
};

export const getSerialRecordLog = (serialRecordInput, userName, type, item, recordFrom, order_id, both) => {
  const temp = {
    clicked: type === 'clickscan',
    barcode: item?.barcodes?.[0]?.barcode,
    order_id,
    order_item_id: item?.order_item_id,
    product_lot_id: null,
    ask: recordFrom === 'recordOne',
    ask_2: recordFrom === 'recordTwo',
    product_id: item?.product_id,
    is_scan: both === false ? true : recordFrom === 'recordOne' ? false : true,
    second_serial: both === false ? false : recordFrom === 'recordOne' ? false : true,
    box_id: null,
    serial: serialRecordInput,
    event: 'serial_scan',
    on_ex: 'on GPX'
  };

  const localLog = {
    order_item_id: item?.order_item_id,
    time: new Date(),
    event: 'serial_scan',
    SKU: item?.sku ?? '',
    name: userName
  };

  return { temp, localLog };
};

export const getByPassLog = (userName, order) => {
  const unscannedItems = order.unscanned_items || [];
  const firstUnScannedItem = unscannedItems[0];
  const SKU = firstUnScannedItem?.child_items?.length > 0
    ? firstUnScannedItem.child_items[0].sku
    : firstUnScannedItem?.sku;

  return {
    event: 'by_passed',
    id: order.id,
    name: userName,
    time: new Date(),
    Log_count: '',
    SKU
  };
};

export const getTrackingLog = (order, state, event, input) => {
  return {
    id: order.id,
    input,
    state,
    event,
    updated_at: order.updated_at,
    increment_id: order.increment_id
  };
};

// `wmsAnswers` carries every WMS gate's answer fields, threaded straight through to top-level
// keys the backend reads off the scan log entry — see
// ScanPack::ScanBarcodeService#do_if_packing_count_not_present, which slices these params off
// alongside `product_id`/`kit_product_id` before handing off to ScanPack::ProductScanService.
// `undefined` on any of these is indistinguishable from "not yet answered" on the backend
// (`wms_extra` returns nil, `.to_s.strip` => `''`), so it is safe to always thread every key
// through rather than gate its presence — omitting an unanswered field vs. sending it as
// `undefined` behaves identically server-side.
//
// This was a single trailing `wmsLotAnswer` positional until the Z1-Z3 ladder added four more
// answer fields (2026-08-07) — a 6th/7th/8th/9th positional would be unreadable and easy to
// mis-order at call sites, so all WMS answer fields now travel together as one trailing options
// object instead. `wms_lot_answer` (L2/L3) is unaffected in shape/behavior, only in how the
// caller passes it in.
//
// | wmsAnswers key         | emitted log key            | ladder  |
// |-------------------------|-----------------------------|---------|
// | wmsLotAnswer            | wms_lot_answer               | L2/L3   |
// | wmsZeroReceiveLot        | wms_zero_receive_lot         | Z3      |
// | wmsZeroReceiveQty        | wms_zero_receive_qty         | Z3      |
// | wmsZeroEscalationCode    | wms_zero_escalation_code     | Z3      |
// | wmsZeroBypass            | wms_zero_bypass              | Z3      |
// `matchBarcodeValue` must stay the GS1-stripped GTIN because `item.barcodes` entries are bare
// GTINs. It defaults to `barcodeValue`, which is the same string for a non-GS1 scan.
export const getRegularScanLog = (order, item, barcodeValue, userName, Box, kitChild = false, wmsAnswers = {}, matchBarcodeValue = barcodeValue) => {
  const isClickScan = !barcodeValue || barcodeValue.trim() === '';
  const input = isClickScan ? getFirstBarcode(item, '') : barcodeValue;
  const {
    wmsLotAnswer,
    wmsZeroReceiveLot,
    wmsZeroReceiveQty,
    wmsZeroEscalationCode,
    wmsZeroBypass
  } = wmsAnswers;

  return {
    ...getScanPackLog(),
    input,
    id: order.id,
    product_id: item?.product_id || 0,
    order_item_id: item?.order_item_id,
    box_name: Box,
    rem_qty: item?.qty_remaining || null,
    qty_rem: Math.max(0, (item?.qty_remaining || 0) - 1),
    SKU: item?.sku || '',
    Log_count: getPackingCount(item, matchBarcodeValue) || '',
    product_name: item?.name || '',
    name: userName,
    actionBarcode: false,
    state: 'scanpack.rfp.default',
    increment_id: order.increment_id,
    event: isClickScan ? 'click_scan' : 'regular',
    updated_at: order.updated_at,
    is_kit: kitChild || item?.is_kit || false,
    kit_product_id: kitChild ? item?.product_id : (item?.kit_product_id || null),
    wms_lot_answer: wmsLotAnswer,
    wms_zero_receive_lot: wmsZeroReceiveLot,
    wms_zero_receive_qty: wmsZeroReceiveQty,
    wms_zero_escalation_code: wmsZeroEscalationCode,
    wms_zero_bypass: wmsZeroBypass
  };
};

export const getRemoveItemLog = (order, item, scannedBarcode, userName, Box) => {
  const scannedItems = order.scanned_items || [];
  const isKit = item?.product_type === 'individual' && item?.child_items?.length > 0;
  const childItem = isKit ? item.child_items[0] : null;

  return {
    ...getScanPackLog(),
    input: scannedBarcode,
    id: order.id,
    product_id: item?.product_id || 0,
    kit_product_id: childItem?.product_id || null,
    is_kit: false,
    order_item_id: scannedItems.length > 0 ? scannedItems[0].order_item_id : '',
    box_name: Box,
    rem_qty: null,
    qty_rem: 0,
    SKU: childItem?.sku || item?.sku || '',
    name: userName,
    actionBarcode: true,
    removeSkipableItem: false,
    state: 'scanpack.rfp.default',
    increment_id: order.increment_id,
    event: 'regular',
    updated_at: order.updated_at
  };
};

export const getWrongScanLog = (barcodeValue, item, order, userName) => {
  return {
    ...getScanPackLog(),
    Log_count: '',
    input: barcodeValue,
    id: order.id,
    SKU: item?.sku || '',
    qty_rem: item?.qty_remaining || '',
    rem_qty: null,
    increment_id: order.increment_id,
    name: userName,
    state: 'scanpack.rfp.default',
    event: 'regular',
    updated_at: order.updated_at,
    order_item_id: item?.order_item_id,
    product_name: item?.name || '',
    is_kit: item?.is_kit || false,
    kit_product_id: item?.kit_product_id || null
  };
};

export const getPartialCompleteLog = (order, barcodeValue, userName) => {
  return {
    ...getScanPackLog(),
    input: barcodeValue,
    id: order.id,
    state: 'scanpack.rfp.default',
    on_ex: 'on GPX',
    event: 'regular',
    actionBarcode: true,
    name: userName,
    increment_id: order.increment_id,
    updated_at: order.updated_at
  };
};

export const getSkipLog = (order, item, scanPackSettings, userName, Box) => {
  return {
    ...getScanPackLog(),
    input: 'SKIP',
    id: order.id,
    product_id: item?.product_id || 0,
    order_item_id: item?.order_item_id,
    box_name: Box,
    rem_qty: item?.qty_remaining || null,
    qty_rem: item?.qty_remaining || 0,
    SKU: item?.sku || '',
    name: userName,
    actionBarcode: true,
    removeSkipableItem: !!scanPackSettings?.remove_skipped,
    state: 'scanpack.rfp.default',
    increment_id: order.increment_id,
    event: 'regular',
    updated_at: order.updated_at
  };
};

export const getScannedCompleteLog = (order, barcodeValue, userName) => {
  return {
    ...getScanPackLog(),
    input: barcodeValue,
    id: order.id,
    state: 'scanpack.rfp.default',
    on_ex: 'on GPX',
    event: 'scanned',
    actionBarcode: true,
    name: userName,
    increment_id: order.increment_id,
    updated_at: order.updated_at
  };
};

export const buildBulkScanLog = (currentOrder, scanPackSettings, currentItem, unScannedItems, bulkType, userName) => {
  const firstItem = unScannedItems[0];
  const scanType = bulkType !== 'scan_all_items' ? 'bulk_scan' : 'scan_all_items';
  const productIdChild = firstItem?.child_items?.[0]?.product_id;
  const qtyToScan = currentItem.qty_remaining || 0;
  return getScanAllItemLog(
    currentOrder,
    scanPackSettings?.type_scan_code || '',
    currentOrder.next_state || 'scanpack.rfp.default',
    scanType,
    firstItem,
    currentItem.sku,
    userName,
    (currentItem.scanned_qty || 0) + qtyToScan,
    currentItem.scanned_qty || 0,
    productIdChild
  );
};
